import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  NavLink,
  Link,
  useLocation,
  useRouteMatch,
  useHistory,
} from 'react-router-dom';
import classNames from 'classnames';
import {
  locationToShowMainPage,
  useIsShowingFullscreenOverlay,
} from '#app/containers/routes';
import { useSelectedProfileId } from '#modules/state/ProfileState';
import ClipboardCopyButton from '#components/Elements/ClipboardCopyButton/ClipboardCopyButton';
import ActivityIndicator from '#components/Pages/Overview/ActivityTable/ActivityIndicator';
import { useProfile, useTokenizedApi } from '@tokenized/sdk-react-private';
import { useSubscribedProfiles } from '#network/Billing';
import {
  useUnreadMessagesCount,
  useAnyProviderEntity,
  useAnyProviderLoggedIn,
  useLogout,
} from '#modules/api/User';
import EntityIcon from '#components/Elements/EntityAvatar/EntityIcon';
import ActivityIcon from '#components/Elements/SVGIcon/ActivityIcon';
import PortfolioIcon from '#components/Elements/SVGIcon/ContractsIcon';
import GiftCardIcon from '#components/Elements/SVGIcon/GiftCardIcon';
import PlanIcon from '#components/Elements/SVGIcon/PlanIcon';
import CurrencyInstrumentIcon from '#components/Elements/SVGIcon/CurrencyInstrumentIcon';
import BankAccountIcon from '#components/Elements/SVGIcon/BankAccountIcon';
import CoinsStackedIcon from '#components/Elements/SVGIcon/CoinsStackedIcon';
import GBuyIcon from '#components/Elements/SVGIcon/GBuyIcon';
import InboxIcon from '#components/Elements/SVGIcon/EnvelopeIcon';
import Tooltip from '#components/Elements/Tooltip/Tooltip';
import Button from '#components/Elements/Button/Button';
import LogOutIcon from '#components/Elements/SVGIcon/LogOutIcon';
import DocumentIcon from '#components/Elements/SVGIcon/DocumentIcon';
import ReportsIcon from '#components/Elements/SVGIcon/ReportsIcon';
import FourCirclesIcon from '#components/Elements/SVGIcon/FourCirclesIcon';
import { isLiquidNobleLoggedIn } from '#modules/utils/jwt';
import styles from './NavigationBar.scss';

function ProfileIcon({ profile, selected }) {
  const history = useHistory();
  const location = useLocation();
  const overlayVisible = useIsShowingFullscreenOverlay();
  const entity = useAnyProviderEntity(profile.id, profile.entityId);
  const entityName = entity?.name;
  const entityHandle = entity?.handle;

  return (
    <Tooltip text={entity?.name} location="east">
      <button
        className={classNames(
          styles.workspace,
          !overlayVisible && '',
          selected && styles.selected,
        )}
        // onClick={() => {
        //   history.push(
        //     locationToShowMainPage(
        //       locationToSwitchProfile(
        //         location,
        //         profile.id === personalProfileId ? undefined : profile.entityId,
        //       ),
        //       '/overview/activity-history',
        //     ),
        //   );
        // }}
      >
        <div className={styles.profileContainer}>
          <div className={styles.avatarContainer}>
            <EntityIcon
              src={entity?.avatarUrl}
              className={styles.avatar}
              size="medium"
              displayName={entity?.name}
              inverted
            />
          </div>
          <div className={styles.infoContainer}>
            <h2>{entityName}</h2>
            <ClipboardCopyButton
              className={styles.entityHandle}
              wrapperClassName={classNames(!overlayVisible && 'no-drag')}
              value={entityHandle}
              inverted
              tooltipLocation="south"
              test="handle"
            />
          </div>
        </div>
        <ActivityIndicator
          className={styles.activityIndicator}
          profileId={profile.id}
        >
          <div className={styles.activity}></div>
        </ActivityIndicator>
      </button>
    </Tooltip>
  );
}

export default function NavigationBar() {
  const location = useLocation();
  const overlayVisible = useIsShowingFullscreenOverlay();
  const isLoggedIn = useAnyProviderLoggedIn();
  const selectedProfileId = useSelectedProfileId();
  const profiles = useSubscribedProfiles() || [];
  const isLqn = isLiquidNobleLoggedIn();
  const treasuryDetailsRouteMatch = useRouteMatch('/instrument/:instrumentId');
  const treasuryRouteMatch = useRouteMatch('/treasury');
  const inboxRouteMatch = useRouteMatch('/inbox');

  const profile = useProfile(useSelectedProfileId());

  const tokenizedApi = useTokenizedApi();
  const onLogout = useLogout();
  const logOut = useCallback(() => {
    onLogout();
    tokenizedApi.account.logOut();
  }, [tokenizedApi.account, onLogout]);

  return (
    <>
      <nav className={styles.navbar} data-test="navigation">
        <header className={styles.header}>
          {isLoggedIn && (
            <>
              {profiles
                .filter((profile) => profile.id === selectedProfileId)
                .map((profile) => (
                  <ProfileIcon key={profile.id} profile={profile} />
                ))}
            </>
          )}
        </header>

        <div className={styles.mainPages}>
          <NavLink
            className={styles.link}
            activeClassName={classNames(styles.link, styles.active)}
            to={locationToShowMainPage(location, '/dashboard')}
          >
            <FourCirclesIcon className={styles.icon} size="tiny" />
            <FormattedMessage
              defaultMessage="Home"
              description="Main Dashboard link"
            />
          </NavLink>
          <NavLink
            className={styles.link}
            activeClassName={classNames(styles.link, styles.active)}
            to={locationToShowMainPage(location, '/gbuy')}
          >
            <GBuyIcon className={styles.icon} size="tiny" />
            <FormattedMessage
              defaultMessage="gBuy"
              description="Main navigation bar link"
            />
          </NavLink>
          <NavLink
            className={styles.link}
            activeClassName={classNames(styles.link, styles.active)}
            to={locationToShowMainPage(location, '/banking')}
          >
            <BankAccountIcon className={styles.icon} size="tiny" />
            <FormattedMessage
              defaultMessage="Bank accounts"
              description="Main navigation bar link"
            />
          </NavLink>
          {/* <NavLink
            className={styles.link}
            activeClassName={classNames(styles.link, styles.active)}
            to={locationToShowMainPage(location, '/documents')}
          >
            <DocumentIcon className={styles.icon} size="tiny" />
            <FormattedMessage
              defaultMessage="Documents"
              description="Main navigation bar link"
            />
          </NavLink> */}
          <NavLink
            className={styles.link}
            activeClassName={classNames(styles.link, styles.active)}
            to={locationToShowMainPage(location, '/holdings')}
          >
            <PortfolioIcon className={styles.icon} size="tiny" />
            <FormattedMessage
              defaultMessage="My Holdings"
              description="Main navigation bar link"
            />
          </NavLink>
          <NavLink
            className={styles.link}
            activeClassName={classNames(styles.link, styles.active)}
            to={locationToShowMainPage(location, '/gift-cards')}
          >
            <GiftCardIcon className={styles.icon} size="tiny" />
            <FormattedMessage
              defaultMessage="Gift Cards"
              description="Main navigation bar link"
            />
          </NavLink>
          <NavLink
            className={styles.link}
            activeClassName={classNames(styles.link, styles.active)}
            to={locationToShowMainPage(location, '/plan')}
          >
            <PlanIcon className={styles.icon} size="tiny" />
            <FormattedMessage
              defaultMessage="Accumulation Plans"
              description="Main navigation bar link"
            />
          </NavLink>
          <NavLink
            className={styles.link}
            activeClassName={classNames(styles.link, styles.active)}
            to={locationToShowMainPage(location, '/transactions')}
          >
            <ActivityIcon className={styles.icon} size="tiny" />
            <FormattedMessage
              defaultMessage="Transactions"
              description="Main navigation bar link"
            />
            <ActivityIndicator
              profileId={profile?.id}
              className={styles.activityIndicator}
            />
          </NavLink>
          {/* <NavLink
            className={styles.link}
            activeClassName={classNames(styles.link, styles.active)}
            to={locationToShowMainPage(location, '/digital-currencies')}
          >
            <CurrencyInstrumentIcon className={styles.icon} size="tiny" />
            <FormattedMessage
              defaultMessage="Digital Currencies"
              description="Main navigation bar link"
            />
          </NavLink> */}
          <NavLink
            className={styles.link}
            activeClassName={classNames(styles.link, styles.active)}
            to={locationToShowMainPage(location, '/statements')}
          >
            <DocumentIcon className={styles.icon} size="tiny" />
            <FormattedMessage
              defaultMessage="Statements"
              description="Main navigation bar link"
            />
          </NavLink>
          <NavLink
            className={styles.link}
            activeClassName={classNames(styles.link, styles.active)}
            to={locationToShowMainPage(location, '/reports')}
          >
            <ReportsIcon className={styles.icon} size="tiny" />
            <FormattedMessage
              defaultMessage="CGT Reports"
              description="Main navigation bar link"
            />
          </NavLink>
          <NavLink
            className={styles.link}
            activeClassName={classNames(styles.link, styles.active)}
            to={locationToShowMainPage(location, '/redemption')}
          >
            <CoinsStackedIcon className={styles.icon} size="tiny" />
            <FormattedMessage
              defaultMessage="Redemption"
              description="Main navigation bar link"
            />
          </NavLink>
          <Link
            className={classNames(
              styles.link,
              inboxRouteMatch && styles.active,
            )}
            to={locationToShowMainPage(location, '/inbox/messages')}
          >
            <InboxIcon className={styles.icon} size="tiny" />
            <FormattedMessage
              defaultMessage="Inbox"
              description="Main navigation bar link"
            />
            <ActivityIndicator
              profileId={profile?.id}
              className={styles.activityIndicator}
              useActivityHook={useUnreadMessagesCount}
            />
          </Link>
        </div>
        <footer className={styles.footer}>
          {/* <NavLink
            className={styles.link}
            to={locationToOpenSettings(
              location,
              'workspace/profile',
              entityIsIndividual ? null : entity?.id,
            )}
            data-test="workspace-settings"
          >
            <SettingsIcon className={styles.icon} size="tiny" />
            <FormattedMessage
              defaultMessage="Workspace settings"
              description="Main navigation bar footer link"
            />
          </NavLink> */}
          <Button text onClick={logOut} className={styles.logOut}>
            <LogOutIcon /> Log out
          </Button>
        </footer>
      </nav>
    </>
  );
}
